/**
 * FacetManager - Handles facet configuration and processing for Algolia collections
 *
 * This class manages:
 * - Collection-specific facet detection
 * - Facet filtering (enabled, hidden, category filter exclusion)
 * - Facet configuration processing
 * - Open filters management
 */

class FacetManager {
  constructor(algolia, options = {}) {
    this.algolia = algolia;
    this.categoryFilter = options.categoryFilter || '';
    this.categoryFilterName = options.categoryFilterName || '';
    this.openFilters = options.openFilters || [];

    // Store processed facets
    this.allEnabledFacets = [];
    this.hiddenFacets = [];

    // Color-related properties
    this.colorMappings = options.colorMappings || [];
    this.lightColorMappings = options.lightColorMappings || '';
    this.enableResultCount = options.enableResultCount || false;
    this.enableCollapsibleFilters = options.enableCollapsibleFilters || true;
  }

  /**
   * Get the appropriate facets for the current collection
   * @returns {Array} Array of processed facets ready for rendering
   */
  getCollectionFacets() {
    // Check if collection_facets is available in the config
    if (!this.algolia.config.collection_facets) {
      // Fallback to regular facets if collection_facets is not available
      return this.processRegularFacets();
    }

    // Get current collection ID or handle
    const currentCollectionId = this.algolia.current_collection_id;
    const currentCollectionHandle = this.getCollectionHandle();

    // Try to get collection-specific facets by ID first, then handle, then default
    let collection_facets = null;

    if (currentCollectionId && this.algolia.config.collection_facets[currentCollectionId]) {
      collection_facets = this.algolia.config.collection_facets[currentCollectionId];
    } else if (currentCollectionHandle && this.algolia.config.collection_facets[currentCollectionHandle]) {
      collection_facets = this.algolia.config.collection_facets[currentCollectionHandle];
    } else if (this.algolia.config.collection_facets.default) {
      collection_facets = this.algolia.config.collection_facets.default;
    } else {
      collection_facets = this.algolia.config.facets;
    }

    if (collection_facets) {
      return this.processCollectionFacets(collection_facets);
    }

    // Final fallback to regular facets
    return this.processRegularFacets();
  }

  /**
   * Process collection-specific facets
   * @param {Array} collection_facets - Raw collection facets from config
   * @returns {Array} Processed facets ready for rendering
   */
  processCollectionFacets(collection_facets) {
    // Filter to only enabled facets (same logic as provided code)
    const enabledCollectionFacets = collection_facets.filter(facet => {
      return facet.enabled || parseInt(facet.enabled, 10);
    });

    // Store all enabled facets (including hidden ones) for potential use
    this.allEnabledFacets = enabledCollectionFacets.map(facet => {
      return {
        ...facet,
        escapedName: encodeURIComponent(facet.name)
      };
    });

    // Separate hidden facets (for potential use in search configuration)
    this.hiddenFacets = enabledCollectionFacets.filter(facet => {
      return facet.type === 'hidden';
    });

    // Return only non-hidden facets for rendering, excluding the category filter
    const processedFacets = this.allEnabledFacets.filter(facet => {
      // Exclude hidden facets
      if (facet.type === 'hidden') {
        return false;
      }

      // Exclude the category filter to avoid duplication with the category slider
      if (this.categoryFilter && (facet.name === this.categoryFilter || facet.attribute === this.categoryFilter)) {
        return false;
      }

      return true;
    });

    return processedFacets;
  }

  /**
   * Process regular facets (fallback)
   * @returns {Array} Processed facets ready for rendering
   */
  processRegularFacets() {
    // Process regular facets with the same logic
    const regularFacets = this.algolia.config.facets || [];

    // Filter to only enabled facets
    const enabledFacets = regularFacets.filter(facet => {
      return facet.enabled || parseInt(facet.enabled, 10);
    });

    // Store all enabled facets (including hidden ones) for potential use
    this.allEnabledFacets = enabledFacets.map(facet => {
      return {
        ...facet,
        escapedName: encodeURIComponent(facet.name)
      };
    });

    // Separate hidden facets (for potential use in search configuration)
    this.hiddenFacets = enabledFacets.filter(facet => {
      return facet.type === 'hidden';
    });

    // Return only non-hidden facets for rendering, excluding the category filter
    const processedFacets = this.allEnabledFacets.filter(facet => {
      // Exclude hidden facets
      if (facet.type === 'hidden') {
        return false;
      }

      // Exclude the category filter to avoid duplication with the category slider
      if (this.categoryFilter && (facet.name === this.categoryFilter || facet.attribute === this.categoryFilter)) {
        return false;
      }

      return true;
    });

    return processedFacets;
  }

  /**
   * Get the collection handle from the current URL
   * @returns {string|null} Collection handle or null if not found
   */
  getCollectionHandle() {
    return AlgoliaSharedUtils.getCollectionHandle();
  }

  /**
   * Check if a facet should be open by default based on configuration
   * @param {string} facetTitle - The title of the facet
   * @returns {boolean} Whether the facet should be open by default
   */
  shouldFacetBeOpenByDefault(facetTitle) {
    if (!this.openFilters || this.openFilters.length === 0) return false;

    // Normalize the facet title for comparison (lowercase to match processed data)
    const normalizedFacetTitle = facetTitle.toLowerCase().trim();

    const shouldOpen = this.openFilters.some(filterName => {
      const normalizedFilterName = filterName.toLowerCase().trim();
      // Only exact matches - no partial matching
      return normalizedFacetTitle === normalizedFilterName;
    });
    return shouldOpen;
  }

  /**
   * Parse the open filters data from configuration
   * @param {string} openFiltersData - Comma-separated string of filter names
   * @returns {Array} Array of filter names that should be open by default
   */
  parseOpenFilters(openFiltersData) {
    // Parse the open filters data from the data attribute
    if (!openFiltersData) return [];

    try {
      // Since the Liquid template processes it as comma-separated and lowercase,
      // we split by comma and trim each value
      return openFiltersData
        .split(',')
        .map(filter => filter.trim())
        .filter(filter => filter.length > 0);
    } catch (error) {
      AlgoliaSharedUtils.logError('Error parsing open filters:', error);
      return [];
    }
  }

  /**
   * Format attribute name for display
   * @param {string} attribute - The raw attribute name
   * @returns {string} Formatted attribute name
   */
  formatAttributeName(attribute) {
    // Format attribute name for display
    if (!attribute) return '';

    let formattedName = attribute
      .replace(/^meta\.custom_fields\./, '')  // Remove meta.custom_fields prefix
      .replace(/^named_tags\./, '')           // Remove named_tags prefix
      .replace(/^tags\./, '')                 // Remove tags prefix
      .split('.').pop()                       // Get the part after the last dot
      .replace(/_/g, ' ')                     // Replace underscores with spaces
      .replace(/\b\w/g, l => l.toUpperCase()) // Capitalize each word
      .replace(/\s+/g, ' ')                   // Normalize multiple spaces to single space
      .trim();                                // Remove leading/trailing spaces

    // Special case: replace "Key" with "Feature" for named_tags.key
    if (formattedName === 'Key') {
      formattedName = 'Feature';
    }

    return formattedName;
  }

  /**
   * Get all enabled facets (including hidden ones)
   * @returns {Array} All enabled facets
   */
  getAllEnabledFacets() {
    return this.allEnabledFacets;
  }

  /**
   * Get hidden facets
   * @returns {Array} Hidden facets
   */
  getHiddenFacets() {
    return this.hiddenFacets;
  }

  /**
   * Update category filter configuration
   * @param {string} categoryFilter - The category filter attribute
   * @param {string} categoryFilterName - The category filter name
   */
  updateCategoryFilter(categoryFilter, categoryFilterName) {
    this.categoryFilter = categoryFilter;
    this.categoryFilterName = categoryFilterName;
  }

  /**
   * Update open filters configuration
   * @param {Array} openFilters - Array of filter names that should be open by default
   */
  updateOpenFilters(openFilters) {
    this.openFilters = openFilters || [];
  }

  // ===== COLOR FACET METHODS =====

  checkIfColorFacet(facetName, facetTitle) {
    // If colorFilterName is configured, use exact matching
    if (this.colorFilterName && this.colorFilterName.trim()) {
      const configuredColorFilterName = this.colorFilterName.toLowerCase().trim();
      const normalizedFacetName = facetName.toLowerCase();
      const normalizedFacetTitle = facetTitle.toLowerCase();

      return normalizedFacetName === configuredColorFilterName ||
             normalizedFacetTitle === configuredColorFilterName;
    }

    // Fallback to pattern matching if no colorFilterName is configured
    const normalizedFacetName = facetName.toLowerCase();
    const normalizedFacetTitle = facetTitle.toLowerCase();

    return normalizedFacetName.includes('color') ||
           normalizedFacetName.includes('colour') ||
           normalizedFacetName.includes('flower_colour') ||
           normalizedFacetName === 'named_tags.colour';
  }

  isColorFacetByAttribute(attribute) {
    const facetTitle = this.formatAttributeName(attribute);
    return this.checkIfColorFacet(attribute, facetTitle);
  }

  applyColorSwatchStylingShared(params) {
    // Apply color-specific CSS classes
    const colorCssClasses = {
      ...params.cssClasses,
      list: 'filter-options__box filter-options__box--color options options--color list-unstyled is-flex is-aligned-center',
      item: 'ais-RefinementList-item options__option options__option--color list-menu__item',
      label: 'ais-RefinementList-label options__label options__label--color',
      selectedItem: 'options__option--active',
      checkbox: 'ais-RefinementList-checkbox ais-facet--checkbox visually-hidden',
      showMore: 'filter-options__show-more',
      count: 'hidden'
    };

    params.cssClasses = colorCssClasses;
    // Apply color swatch template following the specified structure
    const colorSwatchTemplate = `
      <div>
        {{#hasValidHexColor}}
          <label class="ais-RefinementList-label options__label options__label--color{{#isLightColor}} options__label--color__border{{/isLightColor}}" style="background-color: {{hexColor}};">
            <input type="checkbox" class="ais-RefinementList-checkbox ais-facet--checkbox visually-hidden" value="{{value}}" {{#isRefined}}checked{{/isRefined}}>
            <span class="visually-hidden">{{label}}</span>
          </label>
        {{/hasValidHexColor}}
        {{^hasValidHexColor}}
          <label class="ais-RefinementList-label options__label options__label--color">
            <input type="checkbox" class="ais-RefinementList-checkbox ais-facet--checkbox visually-hidden" value="{{value}}" {{#isRefined}}checked{{/isRefined}}>
            {{label}}
          </label>
        {{/hasValidHexColor}}
      </div>
    `;

    // Apply template - handle both cases (direct item assignment vs templates object)
    if (params.templates) {
      params.templates.item = colorSwatchTemplate;
      params.templates.showMoreText = this.getShowMoreTemplate();
    } else {
      params.templates = {
        item: colorSwatchTemplate,
        showMoreText: this.getShowMoreTemplate()
      };
    }

    // Add transformItems function to handle color processing
    params.transformItems = (items) => {
      return items.map(item => {
        const originalColorName = item.value || item.label;
        const hexColor = this.getColorValue(originalColorName);
        const hasValidHexColor = this.isValidHexColor(hexColor, originalColorName);
        const isLightColor = this.isLightColor(originalColorName);
        return {
          ...item,
          hexColor: hexColor,
          hasValidHexColor: hasValidHexColor,
          isLightColor: isLightColor
        };
      });
    };
  }

  applyRegularCheckboxStyling(params) {
    // Apply regular checkbox CSS classes
    const cssClasses = {
      ...params.cssClasses,
      list: 'filter-options__list list-options list-options--type list-unstyled',
      item: 'ais-RefinementList-item list-options__option list-options__option--type',
      label: 'ais-RefinementList-label list-options__label list-options__label--type',
      checkbox: 'ais-RefinementList-checkbox ais-facet--checkbox visually-hidden',
      selectedItem: 'list-options__option--active',
      showMore: 'filter-options__show-more'
    };
    // Only include count class if result count is enabled
    if (this.enableResultCount) {
      cssClasses.count = 'ais-RefinementList-count ais-facet--count';
    } else {
      cssClasses.count = 'hidden';
    }

    params.cssClasses = cssClasses;
    // Apply regular checkbox template
    const countSpan = this.enableResultCount ? '<span class="ais-RefinementList-count ais-facet--count">{{count}}</span>' : '';
    const regularCheckboxTemplate = `
      <div>
        <label class="ais-RefinementList-label list-options__label list-options__label--type{{#isRefined}} list-options__label--active{{/isRefined}}">
          <input type="checkbox" class="ais-RefinementList-checkbox ais-facet--checkbox visually-hidden" value="{{value}}" {{#isRefined}}checked{{/isRefined}}>
          <span class="list-options__checkmark">
            <svg class="icon icon-checkmark icon--xxs" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="presentation" viewBox="0 0 24 24">
              <path fill="currentColor" fill-rule="evenodd" clip-rule="evenodd" d="M19.5,8.1l-8.8,8.8c-0.3,0.3-0.8,0.3-1.1,0c0,0,0,0,0,0l-5-5c-0.3-0.3-0.3-0.8,0-1.1s0.8-0.3,1.1,0l4.5,4.5 l8.3-8.3c0.3-0.3,0.8-0.3,1.1,0C19.7,7.4,19.7,7.8,19.5,8.1z"></path>
            </svg>
          </span>
          {{label}}
          ${countSpan}
        </label>
      </div>
    `;

    // Apply template - handle both cases (direct item assignment vs templates object)
    if (params.templates) {
      params.templates.item = regularCheckboxTemplate;
      params.templates.showMoreText = this.getShowMoreTemplate();
    } else {
      params.templates = {
        item: regularCheckboxTemplate,
        showMoreText: this.getShowMoreTemplate()
      };
    }
  }

  getShowMoreTemplate() {
    return `
      <span class="filter-options__show-more__icon filter-options__show-more__icon-algolia">
        {{#isShowingMore}}
          <svg class="icon icon-minus icon--sm" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="presentation" viewBox="0 0 24 24">
            <path fill="currentColor" fill-rule="evenodd" clip-rule="evenodd" d="M18.9,11.2L5.1,11.3c-0.4,0-0.8,0.3-0.8,0.8s0.3,0.8,0.8,0.8l14,0c0.4,0,0.8-0.3,0.8-0.8S19.3,11.2,18.9,11.2z"></path>
          </svg>
        {{/isShowingMore}}
        {{^isShowingMore}}
          <svg class="icon icon-plus icon--sm" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="presentation" viewBox="0 0 24 24">
            <path d="M12 6v6m0 0v6m0-6h6m-6 0H6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        {{/isShowingMore}}
      </span>
      <span class="filter-options__show-more__text label-medium weight-medium" data-toggle-show-more-text="">
        {{#isShowingMore}}Show less{{/isShowingMore}}{{^isShowingMore}}Show more{{/isShowingMore}}
      </span>
    `;
  }

  getColorValue(colorName) {
    if (!colorName) return colorName;

    const cleanColorName = colorName.toLowerCase().trim();

    // If it's already a hex color, return it
    if (cleanColorName.startsWith('#')) {
      return cleanColorName;
    }

    // Search through parsed color mappings to find a direct match
    if (Array.isArray(this.colorMappings) && this.colorMappings.length > 0) {
      for (const mapping of this.colorMappings) {
        if (mapping.label && mapping.color) {
          const mappingLabel = mapping.label.toLowerCase().trim();

          // Try exact match first
          if (cleanColorName === mappingLabel) {
            return mapping.color;
          }

          // Try partial match (in case facet title contains the color name)
          if (cleanColorName.includes(mappingLabel) || mappingLabel.includes(cleanColorName)) {
            return mapping.color;
          }
        }
      }
    }

    // Fallback: Get color mappings from theme settings (legacy support)
    const colorMap = this.getColorMappingsFromTheme();

    // Handleize the color name to match theme setting keys
    const handleizedColorName = this.handleizeColorName(cleanColorName);

    // Check if it's a mapped color name (using handleized version - primary method)
    if (colorMap[handleizedColorName]) {
      return colorMap[handleizedColorName];
    }

    // Check if it's a mapped color name (using original clean name as fallback)
    if (colorMap[cleanColorName]) {
      return colorMap[cleanColorName];
    }
    // For other named colors, try to use CSS color names directly
    return cleanColorName;
  }

  isValidHexColor(hexColor, originalColorName) {
    // Check if we have a valid hex color
    if (!hexColor || !originalColorName) return false;

    // If it's a hex color, it's valid
    if (hexColor.startsWith('#')) {
      return true;
    }

    // If the hex color is the same as the original (no transformation occurred),
    // it means we didn't find a mapping and fell back to CSS color name
    const cleanOriginal = originalColorName.toLowerCase().trim();
    const cleanHex = hexColor.toLowerCase().trim();

    // If they're the same, it means no color mapping was found
    if (cleanHex === cleanOriginal) {
      return false;
    }

    // Otherwise, assume it's a valid CSS color
    return true;
  }

  handleizeColorName(str) {
    return AlgoliaSharedUtils.handleizeColorName(str);
  }

  getLightColorMappings() {
    // Parse light color mappings from theme settings
    if (!this.lightColorMappings) return [];

    try {
      return this.lightColorMappings
        .split(/[,\n]/)
        .filter(color => color.trim())
        .map(color => this.handleizeColorName(color.trim()));
    } catch (error) {
      AlgoliaSharedUtils.logError('Error parsing light color mappings:', error);
      return [];
    }
  }

  isLightColor(colorName) {
    if (!colorName) return false;

    const lightColors = this.getLightColorMappings();
    const handleizedColorName = this.handleizeColorName(colorName.toLowerCase().trim());

    return lightColors.includes(handleizedColorName);
  }

  getColorMappingsFromTheme() {
    // Use the parsed colorMappings array if available
    if (Array.isArray(this.colorMappings) && this.colorMappings.length > 0) {
      const colorMap = {};
      this.colorMappings.forEach(mapping => {
        if (mapping.label && mapping.color) {
          // Handleize the mapping label to ensure consistent matching
          const handleizedLabel = this.handleizeColorName(mapping.label);
          colorMap[handleizedLabel] = mapping.color;
        }
      });
      return colorMap;
    }

    // Fallback colors if colorMappings is not available (also handleized)
    return {
      'red': '#FF0000',
      'blue': '#0000FF',
      'green': '#008000',
      'yellow': '#FFFF00',
      'orange': '#FFA500',
      'purple': '#800080',
      'pink': '#FFC0CB',
      'brown': '#A52A2A',
      'black': '#000000',
      'white': '#FFFFFF',
      'gray': '#808080',
      'grey': '#808080',
    };
  }


  // ===== UI/DISPLAY METHODS =====

  createFilterContainerHTML(title, contentId, options = {}) {
    const {
      containerClass = '',
      containerId = '',
      contentAttributes = '',
      shouldBeOpenByDefault = false
    } = options;

    const containerClassAttr = containerClass ? ` class="${containerClass}"` : '';
    const containerIdAttr = containerId ? ` id="${containerId}"` : '';
    const openByDefaultAttr = shouldBeOpenByDefault ? ' data-open-by-default="true"' : '';

    if (this.enableCollapsibleFilters) {
      return `
        <div${containerClassAttr}${containerIdAttr}>
          <div class="filter-options__details" data-collapsible${openByDefaultAttr}>
            <button type="button" class="filter-options__summary is-flex is-justified-space-between is-aligned-center" data-collapsible-trigger>
              <span class="label-medium weight-medium">${title}</span>
              <span class="filter-options__show-more__icon">
                <svg class="icon icon-plus" viewBox="0 0 24 24" width="16" height="16">
                  <path d="M12 6v6m0 0v6m0-6h6m-6 0H6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
                <svg class="icon icon-minus" viewBox="0 0 24 24" width="16" height="16">
                  <path d="M6 12h12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </span>
            </button>
            <div class="filter-options__submenu" data-collapsible-content>
              <div class="facet-content" id="${contentId}"${contentAttributes}></div>
            </div>
          </div>
        </div>
      `;
    } else {
      return `
        <div${containerClassAttr}${containerIdAttr}>
          <div class="filter-options__details">
            <h3 class="label-medium weight-medium filter-options__title">${title}</h3>
            <div class="facet-content" id="${contentId}"${contentAttributes}></div>
          </div>
        </div>
      `;
    }
  }

  generateContainerId(attribute) {
    // Generate a consistent container ID based on the attribute
    return `filter-${attribute.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`;
  }

  hideEmptyFacet(containerSelector) {
    // Hide the facet container if it has no items
    const container = document.querySelector(containerSelector);
    if (!container) return;

    const facetItems = container.querySelectorAll('.ais-RefinementList-item, .ais-Menu-item, .ais-RangeSlider, .ais-ToggleRefinement, .ais-HierarchicalMenu-item');
    const hasItems = facetItems.length > 0;

    // Find the wrapper container (parent of the facet content)
    const wrapper = container.closest('.filter-option');
    if (!wrapper) return;

    const facetTitle = wrapper.querySelector('.filter-options__title, .label-medium')?.textContent?.trim() || 'Unknown';

    wrapper.style.display = hasItems ? '' : 'none';
    wrapper.classList.toggle('facet-empty', !hasItems);
  }

  updateShowMoreButtonVisibility(containerSelector, hasMoreToShow, widgetType) {
    const container = document.querySelector(containerSelector);
    if (!container) return;

    const showMoreButton = container.querySelector('.filter-options__show-more');
    if (!showMoreButton) return;

    // Add 'hidden' class if button has disabled class
    const isDisabled = showMoreButton.classList.contains('ais-RefinementList-showMore--disabled');
    showMoreButton.classList.toggle('hidden', isDisabled);
  }

  handleDisabledShowMoreButtons() {
    // Find all show more buttons and handle disabled states
    const showMoreButtons = document.querySelectorAll('.ais-RefinementList-showMore, .ais-Menu-showMore, .ais-HierarchicalMenu-showMore, .filter-options__show-more');

    showMoreButtons.forEach(button => {
      const isDisabled = button.classList.contains('ais-RefinementList-showMore--disabled') ||
                       button.classList.contains('ais-Menu-showMore--disabled') ||
                       button.classList.contains('ais-HierarchicalMenu-showMore--disabled');

      button.classList.toggle('hidden', isDisabled);
    });
  }

  handleShowMoreClick(showMoreButton) {
    // Get the current number of visible items before the click
    const facetContainer = showMoreButton.closest('.ais-RefinementList, .ais-Menu');
    if (!facetContainer) return;

    const currentItems = facetContainer.querySelectorAll('.ais-RefinementList-item, .ais-Menu-item');
    const currentItemCount = currentItems.length;

    // Use setTimeout to allow Algolia to process the click and update the DOM
    setTimeout(() => {
      const collapsibleContent = showMoreButton.closest('[data-collapsible-content]');
      if (!collapsibleContent) return;

      // Check the new number of items after Algolia processed the click
      const newItems = facetContainer.querySelectorAll('.ais-RefinementList-item, .ais-Menu-item');
      const newItemCount = newItems.length;

      const isExpanding = newItemCount > currentItemCount;

      if (isExpanding) {
        // More items are now visible, so set height to auto for full expansion
        collapsibleContent.style.height = 'auto';
        collapsibleContent.setAttribute('data-expanded', 'true');
      } else {
        // Fewer items are now visible, calculate proper height for the collapsed state
        collapsibleContent.style.height = 'auto';
        // Force reflow to get accurate height
        collapsibleContent.offsetHeight;
        const newHeight = collapsibleContent.scrollHeight;
        collapsibleContent.style.height = newHeight + 'px';
        collapsibleContent.removeAttribute('data-expanded');
      }
    }, 150);
  }
}

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = FacetManager;
} else if (typeof window !== 'undefined') {
  window.FacetManager = FacetManager;
}
